import { cn } from '@Utils/cn';
import type { FlowStageInfo, FlowStageStatus } from '../types';

export interface FlowDetailedViewProps {
  stages: FlowStageInfo[];
  // activeStageId: string | null;
  children?: React.ReactNode;
  className?: string;
}

const STATUS_STYLES: Record<FlowStageStatus, string> = {
  previous: 'border-zinc-300 bg-zinc-50 text-zinc-500',
  active: 'border-[#caab6a] bg-[#caab6a]/5 text-black',
  next: 'border-zinc-200 bg-white text-zinc-400',
  upcoming: 'border-zinc-200 bg-white text-zinc-400',
};

const STATUS_DOT: Record<FlowStageStatus, string> = {
  previous: 'bg-zinc-400',
  active: 'bg-[#caab6a]',
  next: 'bg-zinc-300',
  upcoming: 'bg-zinc-300',
};

const STATUS_LABEL: Record<FlowStageStatus, string> = {
  previous: 'Completed',
  active: 'In Progress',
  next: 'Up Next',
  upcoming: 'Upcoming',
};

export function FlowDetailedView({
  stages,
  children,
  className,
}: FlowDetailedViewProps) {
  return (
    <div
      className={cn(
        'scrollbar-dark flex flex-1 gap-8 bg-zinc-50 px-10 py-8',
        className,
      )}
    >
      <div className="flex w-80 shrink-0 flex-col gap-3">
        <h3 className="mb-1 text-sm font-semibold tracking-wide text-zinc-500 uppercase">
          Stages
        </h3>
        {stages.map((stage, idx) => (
          <div
            key={stage.id}
            className={cn(
              'flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors',
              STATUS_STYLES[stage.status],
            )}
          >
            <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-zinc-200 text-xs font-bold text-zinc-600">
              {idx + 1}
            </div>
            <div className="flex flex-1 flex-col">
              <span className="text-sm font-semibold">{stage.name}</span>
              <div className="flex items-center gap-1.5">
                <span
                  className={cn(
                    'size-2 rounded-full',
                    STATUS_DOT[stage.status],
                  )}
                />
                <span className="text-xs text-zinc-500">
                  {STATUS_LABEL[stage.status]}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="min-w-0 flex-1">
        {children ?? (
          <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-zinc-300 bg-white p-12">
            <p className="text-zinc-400">Select a stage to view details</p>
          </div>
        )}
      </div>
    </div>
  );
}
